import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, UserCheck, X, AlertCircle, Users, BadgeCheck, Loader2, ChevronRight, GraduationCap, ShieldCheck, Layers } from "lucide-react";
import { getApiUrl } from "../../config/api";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface Teacher {
  id: string;
  name: string;
  email?: string;
  role?: string;
  assignedClass?: { id: string; name: string; } | null;
}

interface Class {
  id: string;
  name: string;
  teacher?: { id: string; name: string; email: string; } | null;
  students?: { id: string; name: string; email: string; }[];
}

const ClassTeacher: React.FC = () => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedTeacherId, setSelectedTeacherId] = useState("");
  const [selectedClassDetails, setSelectedClassDetails] = useState<Class | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchTerm] = useState("");
  const [filterAssigned, setFilterAssigned] = useState<"all" | "assigned" | "unassigned">("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const token = localStorage.getItem("token");
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const canAssignClassTeacher = role === "admin" || role === "superadmin";

  const fetchClasses = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl("academics/classes"), { headers: { Authorization: `Bearer ${token}` } });
      setClasses(Array.isArray(res.data) ? res.data : res.data.classes || []);
    } catch { }
  }, [token]);

  const fetchTeachers = useCallback(async () => {
    setLoading(true);
    try {
      const teachersRes = await axios.get(getApiUrl("users/teachers"), { headers: { Authorization: `Bearer ${token}` } });
      const teachersData = Array.isArray(teachersRes.data) ? teachersRes.data : teachersRes.data.teachers || [];

      const res = await axios.get(getApiUrl("academics/classes"), { headers: { Authorization: `Bearer ${token}` } });
      const classesData = Array.isArray(res.data) ? res.data : res.data.classes || [];

      const staffTeachers = teachersData.filter((teacher: Teacher) => (teacher.role || "").toLowerCase() !== "headteacher");
      const teachersWithClasses = staffTeachers.map((teacher: Teacher) => {
        const assignedClass = classesData.find((cls: Class) => cls.teacher?.id === teacher.id);
        return { ...teacher, assignedClass: assignedClass ? { id: assignedClass.id, name: assignedClass.name } : null };
      });
      setTeachers(teachersWithClasses);
    } catch { }
    finally { setLoading(false); }
  }, [token]);

  const fetchClassDetails = useCallback(async (classId: string) => {
    try {
      const res = await axios.get(getApiUrl(`academics/classes/${classId}`), { headers: { Authorization: `Bearer ${token}` } });
      setSelectedClassDetails(res.data);
    } catch { }
  }, [token]);

  useEffect(() => { if (token) { fetchClasses(); fetchTeachers(); } }, [fetchClasses, fetchTeachers, token]);
  useEffect(() => { if (selectedClassId) fetchClassDetails(selectedClassId); else setSelectedClassDetails(null); }, [selectedClassId, fetchClassDetails]);

  const filteredTeachers = teachers.filter((t) => {
    const matchesSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase()) || (t.email && t.email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = filterAssigned === "all" || (filterAssigned === "assigned" && t.assignedClass !== null) || (filterAssigned === "unassigned" && t.assignedClass === null);
    return matchesSearch && matchesFilter;
  });

  const totalPages = Math.ceil(filteredTeachers.length / itemsPerPage);
  const currentTeachers = filteredTeachers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleAssignTeacher = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canAssignClassTeacher) {
      setError("Only school admins can assign class teachers.");
      return;
    }
    if (!selectedClassId || !selectedTeacherId) return;
    setLoading(true); setMessage(""); setError("");
    try {
      await axios.put(getApiUrl(`academics/classes/${selectedClassId}`), { teacherId: selectedTeacherId }, { headers: { Authorization: `Bearer ${token}` } });
      await fetchClasses(); await fetchTeachers();
      setMessage("Class teacher assigned successfully.");
      setTimeout(() => { setMessage(""); setIsModalOpen(false); }, 1500);
    } catch { setError("Failed to assign class teacher. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="page-title-section">
          <div>
            <h1>Faculty Allocation</h1>
            <p className="page-subtitle">
              {canAssignClassTeacher
                ? "Assign teachers to classes and manage class teacher assignments"
                : "Review current class teacher assignments across the school"}
            </p>
          </div>
          {canAssignClassTeacher && (
            <button className="utopia-btn-primary flex items-center gap-2" onClick={() => setIsModalOpen(true)}>
              <UserCheck size={18} /> Append Assignment
            </button>
          )}
        </div>

        {/* Analytics Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 p-8 pt-0">
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm transition-all hover:scale-[1.02]">
            <div className="w-10 h-10 bg-teal-50 text-teal-600 rounded-xl flex items-center justify-center mb-4"><BadgeCheck size={20} /></div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Faculty Capacity</p>
            <h4 className="text-2xl font-black text-slate-800">{teachers.length} <span className="text-xs text-slate-300 font-medium">Experts</span></h4>
          </div>
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm transition-all hover:scale-[1.02]">
            <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mb-4"><Users size={20} /></div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Active Assignments</p>
            <h4 className="text-2xl font-black text-slate-800">{teachers.filter(t => t.assignedClass).length}</h4>
          </div>
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm transition-all hover:scale-[1.02]">
            <div className="w-10 h-10 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center mb-4"><AlertCircle size={20} /></div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Unassigned Experts</p>
            <h4 className="text-2xl font-black text-slate-800">{teachers.filter(t => !t.assignedClass).length}</h4>
          </div>
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm transition-all hover:scale-[1.02]">
            <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center mb-4"><ChevronRight size={20} /></div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Efficiency Rate</p>
            <h4 className="text-2xl font-black text-slate-800">{Math.round((teachers.filter(t => t.assignedClass).length / teachers.length) * 100) || 0}%</h4>
          </div>
        </div>

        <div className="utopia-page-container">
          <div className="utopia-table-header p-8 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-black">Class Teacher Assignments</h2>
              <p className="text-xs text-slate-400 mt-1">
                {canAssignClassTeacher
                  ? "View class teacher assignments and status"
                  : "View-only access. Headteachers cannot assign or change class teachers."}
              </p>
            </div>
            <select className="utopia-input h-14 text-sm font-bold w-56 min-w-[200px]" value={filterAssigned} onChange={e => setFilterAssigned(e.target.value as any)}>
              <option value="all">Full Faculty Spectrum</option>
              <option value="assigned">Assigned Classes Only</option>
              <option value="unassigned">Available Experts Only</option>
            </select>
          </div>

          {loading ? (
            <div className="p-32 text-center text-slate-200"><Loader2 className="animate-spin mx-auto opacity-20" size={40} /></div>
          ) : (
            <>
              <table className="utopia-table group/table">
                <thead>
                  <tr>
                    <th>Expert Identity</th>
                    <th>Academic Assignment</th>
                    <th>Contact Endpoint</th>
                    {canAssignClassTeacher && <th className="text-right">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {currentTeachers.map(t => (
                    <tr key={t.id} className="group/row">
                      <td>
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 font-bold text-xs uppercase shadow-inner group-hover/row:bg-teal-50 group-hover/row:text-teal-600 transition-all">
                            {t.name.charAt(0)}
                          </div>
                          <span className="font-black text-slate-800 uppercase tracking-tight text-sm">{t.name}</span>
                        </div>
                      </td>
                      <td>
                        {t.assignedClass ? (
                          <span className="utopia-badge bg-teal-50 text-teal-600 border border-teal-100 text-[10px] font-black uppercase px-4">
                            {t.assignedClass.name}
                          </span>
                        ) : (
                          <span className="utopia-badge bg-slate-50 text-slate-400 border border-slate-100 text-[10px] font-black uppercase px-4">
                            Null Allocation
                          </span>
                        )}
                      </td>
                      <td className="text-slate-400 text-xs font-medium font-mono lowercase">{t.email}</td>
                      {canAssignClassTeacher && (
                        <td className="text-right">
                          <button className="p-2 text-slate-300 hover:text-teal-600 hover:bg-teal-50 rounded-xl transition-all" onClick={() => { setSelectedTeacherId(t.id); setIsModalOpen(true); }}>
                            <Plus size={16} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="utopia-pagination">
                  <button className="utopia-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}><ChevronRight size={16} className="rotate-180" /></button>
                  <div className="flex gap-2 mx-4">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                      <button key={p} className={`utopia-page-btn ${currentPage === p ? 'active' : ''}`} onClick={() => setCurrentPage(p)}>{p}</button>
                    ))}
                  </div>
                  <button className="utopia-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}><ChevronRight size={16} /></button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {isModalOpen && canAssignClassTeacher && (
        <div className="utopia-modal-overlay" onClick={(e) => e.target === e.currentTarget && setIsModalOpen(false)}>
          <div className="utopia-modal-content max-w-4xl p-0 h-auto flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="utopia-modal-header bg-slate-50 p-8 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center text-teal-600"><ShieldCheck size={24} /></div>
                <div>
                  <h3 className="text-xl font-black text-slate-800 tracking-tight">Assign Class Teacher</h3>
                  <p className="text-xs text-slate-400 font-medium">Link a teacher to a class</p>
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-3 hover:bg-white rounded-full transition-all shadow-sm"
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="p-10 border-r border-slate-50">
                <form onSubmit={handleAssignTeacher} onKeyDown={(e) => e.key === 'Escape' && setIsModalOpen(false)} className="space-y-6">
                  {(message || error) && (
                    <div className={message ? 'utopia-form-success' : 'utopia-form-error'}>
                      {message || error}
                    </div>
                  )}
                  <div className="utopia-form-group">
                    <label className="utopia-label required">Select Class</label>
                    <select
                      className="utopia-input"
                      value={selectedClassId}
                      onChange={e => setSelectedClassId(e.target.value)}
                      required
                      autoFocus
                    >
                      <option value="">Choose a class...</option>
                      {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <span className="utopia-label-hint">Select the class you want to assign a teacher to</span>
                  </div>
                  <div className="utopia-form-group">
                    <label className="utopia-label required">Select Teacher</label>
                    <select
                      className="utopia-input"
                      value={selectedTeacherId}
                      onChange={e => setSelectedTeacherId(e.target.value)}
                      required
                    >
                      <option value="">Choose a teacher...</option>
                      {teachers.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name} {t.assignedClass ? `(Currently: ${t.assignedClass.name})` : "(Available)"}
                        </option>
                      ))}
                    </select>
                    <span className="utopia-label-hint">Teachers already assigned to a class will be reassigned</span>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button
                      type="button"
                      className="utopia-btn-secondary flex-1 py-3"
                      onClick={() => setIsModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="utopia-btn-primary flex-1 py-3 shadow-xl shadow-teal-100 flex items-center justify-center gap-3"
                      disabled={loading}
                    >
                      {loading ? <Loader2 className="animate-spin" size={18} /> : <UserCheck size={18} />}
                      Assign Teacher
                    </button>
                  </div>
                </form>
              </div>

              <div className="bg-slate-50/50 p-10 flex flex-col justify-center border-l border-white">
                <h4 className="text-sm font-bold text-slate-600 mb-6">Class Information</h4>
                {selectedClassDetails ? (
                  <div className="space-y-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center text-teal-600 border border-slate-100"><GraduationCap size={24} /></div>
                      <div>
                        <p className="text-xs font-semibold text-slate-400 mb-1">Class Name</p>
                        <p className="text-xl font-bold text-slate-800">{selectedClassDetails.name}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-white rounded-xl border border-slate-100">
                        <p className="text-xs font-semibold text-slate-400 mb-1">Current Teacher</p>
                        <p className="text-sm font-bold text-slate-700">{selectedClassDetails.teacher?.name || "Not Assigned"}</p>
                      </div>
                      <div className="p-4 bg-white rounded-xl border border-slate-100">
                        <p className="text-xs font-semibold text-slate-400 mb-1">Students</p>
                        <p className="text-sm font-bold text-slate-700">{selectedClassDetails.students?.length || 0}</p>
                      </div>
                    </div>
                    {selectedClassDetails.teacher && (
                      <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl">
                        <p className="text-xs text-amber-700">
                          <strong>Note:</strong> Assigning a new teacher will replace the current teacher for this class.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 space-y-4">
                    <div className="w-16 h-16 bg-white/50 rounded-full border-2 border-dashed border-slate-200 mx-auto flex items-center justify-center">
                      <Layers size={24} className="text-slate-300" />
                    </div>
                    <p className="text-sm font-medium text-slate-400">Select a class to view details</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassTeacher;









